// src/containers/preview/DemoMediaPopup.js
//
// Video / Song / Sing-along for the public demo, using the SAME players the
// portal uses (components/tools/VideoTool and MusicTool). Those players only
// save progress for a signed-in user and return early otherwise, so nothing
// is written for a visitor.
//
// The page still wants to know when a visitor has really watched or listened
// (to tick the step and count it for the tracker). Media events don't bubble,
// but they DO pass through ancestors in the capture phase, so one native
// capture listener on the wrapper hears the player's <video>/<audio> without
// touching the shared players at all.
//
// WHICH BUTTON PLAYS WHAT (home page only)
//   🎵 Song        the song with its lyrics underneath — always the plain
//                  player, even when the song has published lyric timings.
//   🎤 Sing-along  the topic's sing-along video (topicLibrary.singAlongUrl).
//                  If there's no video, or this browser can't load it (a
//                  recorded WebM won't play on iPad / Safari, for example), it
//                  switches to the song with the timed, highlighted lyrics —
//                  the same sing-along player the Sing-along studio publishes.
//                  Both can be switched between by hand when both exist.

import React, { useEffect, useRef, useState } from "react";
import ModalPortal from "../../components/common/ModalPortal";
import VideoTool from "../../components/tools/VideoTool";
import MusicTool from "../../components/tools/MusicTool";
import { trackDemo } from "../../services/demoTracker";

// Seconds of playing that count as "tried it" for the ✓ on the demo card.
const DONE_AFTER = 15;

const TITLES = { video: "🎬 Video", music: "🎵 Song", singalong: "🎤 Sing-along" };

const DemoMediaPopup = ({ type, topic, onClose, onDone }) => {
  const wrapRef = useRef(null);
  const playedRef = useRef(false);
  const doneRef = useRef(false);
  const [earned, setEarned] = useState(null); // coins paid for this step (0 = already had it)

  // Sing-along: "video" (the MP4) or "lyrics" (song + timed lyrics)
  const canVideo = Boolean(topic.singAlongUrl);
  const canLyrics = Boolean(topic.sync && topic.musicUrl);
  const [singMode, setSingMode] = useState(canVideo ? "video" : "lyrics");
  const [videoFailed, setVideoFailed] = useState(false);
  const singModeRef = useRef(singMode);
  singModeRef.current = singMode;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const finish = () => {
      if (doneRef.current) return;
      doneRef.current = true;
      setEarned(Number(onDone?.(type, topic)) || 0);
    };
    const onPlay = () => {
      if (playedRef.current) return;
      playedRef.current = true;
      trackDemo(`${type}_play`, { slug: topic.slug, title: topic.title });
    };
    const onTime = (e) => {
      if ((e.target?.currentTime || 0) >= DONE_AFTER) finish();
    };
    // a <source> that can't load fires "error" on the <source> (it doesn't
    // bubble, but the capture phase still passes through this wrapper)
    const onError = (e) => {
      const tag = String(e.target?.tagName || "").toUpperCase();
      if (type !== "singalong" || singModeRef.current !== "video" || (tag !== "VIDEO" && tag !== "SOURCE")) return;
      setVideoFailed(true);
      if (topic.sync && topic.musicUrl) setSingMode("lyrics");
    };
    el.addEventListener("error", onError, true);
    el.addEventListener("play", onPlay, true);
    el.addEventListener("timeupdate", onTime, true);
    el.addEventListener("ended", finish, true);
    return () => {
      el.removeEventListener("error", onError, true);
      el.removeEventListener("play", onPlay, true);
      el.removeEventListener("timeupdate", onTime, true);
      el.removeEventListener("ended", finish, true);
    };
  }, [type, topic, onDone]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const lyricLines = Array.isArray(topic.lyrics)
    ? topic.lyrics.map((l) => String(l ?? ""))
    : typeof topic.lyrics === "string"
    ? topic.lyrics.split("\n")
    : [];

  return (
    <ModalPortal>
      <div className="pvd-pop" role="dialog" aria-modal="true" aria-label={`${topic.title} ${type}`} onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
        <div className={`pvd-box is-media is-${type}`}>
          <header className="pvd-head">
            <h3>
              {TITLES[type]} <span>· {topic.title}</span>
            </h3>
            <button type="button" className="pvd-x" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </header>

          <div className="pvd-media" ref={wrapRef}>
            {type === "video" && <VideoTool videoUrl={topic.videoUrl} subjectKey="demo" slug={topic.slug} />}
            {type === "singalong" && singMode === "video" && canVideo && (
              <VideoTool key="sing-video" videoUrl={topic.singAlongUrl} subjectKey="demo" slug={topic.slug} progressKey="singalong" starLabel="Sing-along" />
            )}
            {type === "singalong" && singMode === "lyrics" && canLyrics && (
              <MusicTool key="sing-lyrics" musicUrl={topic.musicUrl} subjectKey="demo" slug={topic.slug} lyricSync={topic.sync} title={topic.title} />
            )}
            {type === "singalong" && videoFailed && !canLyrics && (
              <p className="pvd-sing-note is-bad">This sing-along video won't play in this browser. Try the 🎵 Song — the words are underneath.</p>
            )}
            {type === "music" && (
              <>
                <MusicTool musicUrl={topic.musicUrl} subjectKey="demo" slug={topic.slug} lyricSync={null} title={topic.title} />
                {lyricLines.length > 0 && (
                  <div className="pvd-lyrics">
                    {lyricLines.map((line, i) => (line.trim() === "" ? <div key={i} className="pvd-lyrics-gap" /> : <p key={i}>{line}</p>))}
                  </div>
                )}
              </>
            )}
          </div>

          {type === "singalong" && canVideo && canLyrics && (
            <p className="pvd-sing-note">
              {videoFailed ? "The video wouldn't load here, so this is the sing-along with highlighted words. " : ""}
              <button type="button" className="pvd-link" onClick={() => setSingMode((m) => (m === "video" ? "lyrics" : "video"))}>
                {singMode === "video" ? "🎤 Sing along with highlighted words instead" : "🎬 Watch the sing-along video instead"}
              </button>
            </p>
          )}

          {earned !== null && (
            <p className="pvd-earned">
              ✓ Step done{earned > 0 ? ` · +${earned} demo coins 🪙` : ""}
            </p>
          )}
          <p className="pvd-foot">
            In the portal, finishing this earns the topic's {type === "music" ? "🎵" : type === "singalong" ? "🎤" : "🎬"} star and coins — and the teacher sees it on their class page.
          </p>
        </div>
      </div>
    </ModalPortal>
  );
};

export default DemoMediaPopup;
